/**
 * Super-admin banner: the permission rules are not being enforced.
 *
 * WHY THIS INTERRUPTS SOMEONE RATHER THAN SITTING ON A SETTINGS PAGE.
 * The Permissions screen invites a super admin to decide what each role may do, and the natural
 * reading of saving it is "I have restricted these people". Right now that is only half true: the
 * grants drive what the interface offers, but the API still answers a request that gets past the
 * interface. Someone could reasonably tighten permissions, believe access is now controlled, and
 * be wrong about it — which is the kind of misunderstanding that is only discovered afterwards.
 *
 * So it is said once per sign-in, in front of the person who can act on it, and it is dismissible
 * because nagging is how warnings get ignored. Dismissal is per-session (sessionStorage): a new
 * sign-in says it again, because the assumption resets too.
 *
 * Super admins only — "the permission system is not enforcing" is a description of how to bypass
 * the product, and it is not everyone's business.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const DISMISS_KEY = 'keycrew-enforcement-notice-dismissed';

const SecurityNoticeBanner = () => {
  const { securityNotice, isSuperAdmin } = useAuth();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === '1'
  );

  if (!isSuperAdmin || !securityNotice || securityNotice.enforcement_active || dismissed) {
    return null;
  }

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div className="bg-amber-50 border-b-2 border-amber-300 px-6 py-3" data-testid="enforcement-banner">
      <div className="flex items-start gap-3 max-w-6xl mx-auto">
        <span className="text-amber-600 text-lg leading-none mt-0.5 flex-shrink-0">⚠</span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-900">
            Role permissions are not being enforced by the server
          </p>
          <p className="text-xs text-amber-800 mt-1 leading-relaxed">
            Permissions currently decide <strong>what people see</strong> — menus and buttons are
            hidden from roles that lack them. They do <strong>not</strong> block a request that
            reaches the API directly, so they are not yet a security boundary.
            {securityNotice.legacy_login_enabled && (
              <> The shared access password also grants full access to anyone who uses it.</>
            )}
          </p>
          <p className="text-xs text-amber-800 mt-1">
            <Link to="/admin/permissions" className="underline font-medium hover:no-underline">
              Review permissions
            </Link>
            <span className="text-amber-700"> · enforcement mode: <strong>{securityNotice.enforcement_mode}</strong></span>
          </p>
        </div>
        <button onClick={dismiss} aria-label="Dismiss"
          className="text-amber-600 hover:text-amber-800 text-lg leading-none flex-shrink-0">
          ✕
        </button>
      </div>
    </div>
  );
};

export default SecurityNoticeBanner;
